import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { OrganizationType, VendorType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import { batchPromises } from '@shared/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InputComponent, AsyncPipe],
  selector: 'aux-multiple-organizations-dialog',
  styleUrls: ['multiple-organizations-dialog.css'],
  templateUrl: './multiple-organizations-dialog.html',
})
export class MultipleOrganizationsDialogComponent {
  private organizationService = inject(OrganizationService);
  private organizationStore = inject(OrganizationStore);
  private overlayService = inject(OverlayService);

  isLoading$ = new BehaviorSubject(false);
  ref = inject<CustomOverlayRef<unknown, null>>(CustomOverlayRef);
  @ViewChild('vendors') vendors!: InputComponent;

  addVendors = async () => {
    if (!this.vendors.fc.value) {
      this.vendors.fc.markAsTouched();
      this.vendors.fc.setErrors({ emptyVendors: true });

      return;
    }

    const vendorNames: string[] = this.vendors.fc.value
      .split('\n')
      .filter((vendor: string) => vendor);
    const lowerCaseNames = vendorNames.map((vendor: string) => vendor.toLowerCase());
    const existingVendorNames = this.getExistingVendorNames();
    const hasDuplicatedNewNames = lowerCaseNames.length !== new Set(lowerCaseNames).size;
    const hasDuplicatedWithExistingNames = lowerCaseNames.some((vendorName: string) =>
      existingVendorNames.includes(vendorName)
    );

    if (hasDuplicatedNewNames || hasDuplicatedWithExistingNames) {
      this.vendors.fc.setErrors({ duplicatedVendors: true });

      return;
    }

    this.isLoading$.next(true);

    await batchPromises(vendorNames, (vendor) =>
      this.organizationService.add(vendor, {
        costs_included_in_parent_wo: false,
        email: '',
        family_name: '',
        given_name: '',
        parent_organization_id: '',
        phone_number: '',
        receives_vendor_estimate: false,
        title: '',
        vendor_type: VendorType.VENDOR_TYPE_OTHER,
      })
    ).then((results) => {
      let hasErrors = false;

      (results as GraphqlResponse<unknown>[]).forEach(({ errors, success }) => {
        if (!success) {
          this.overlayService.error(errors);
          hasErrors = true;
        }
      });

      if (!hasErrors) {
        this.ref.canDeactivate = () => true;
        this.overlayService.success('Vendor Contracts successfully added!');
        this.ref.close();
      }

      this.isLoading$.next(false);
    });
  };

  canDeactivate = async () => {
    if (this.vendors.fc.value) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  };

  constructor() {
    this.ref.canDeactivate = this.canDeactivate;
  }

  getExistingVendorNames() {
    return Object.values(this.organizationStore.getValue().entities || {})
      .reduce((accumulator, { name, organization_type }) => {
        if (organization_type === OrganizationType.ORGANIZATION_VENDOR) {
          return [...accumulator, name as string];
        }

        return accumulator;
      }, [] as string[])
      .map((vendorName) => vendorName.toLowerCase());
  }
}
